import { useState, useRef, useEffect, useMemo } from 'react';
import * as S from './styles';
import { CoinDetail } from '../types';

interface CoinSelectorProps {
  coinDetail: CoinDetail | undefined;
  coinList: CoinDetail[];
  onSelectCoin: (symbol: string) => void;
}

export function CoinSelector({
  coinDetail,
  coinList,
  onSelectCoin,
}: CoinSelectorProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  // 검색 필터링
  const filteredCoinList = useMemo(() => {
    if (!searchValue.trim()) return coinList;
    const query = searchValue.toLowerCase();
    return coinList.filter(
      (coin) =>
        coin.symbol.toLowerCase().includes(query) ||
        coin.name.toLowerCase().includes(query)
    );
  }, [coinList, searchValue]);

  // 외부 클릭 시 드롭다운 닫기
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
        setSearchValue('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // 드롭다운 열릴 때 검색창 포커스
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen]);

  const handleSelectCoin = (symbol: string) => {
    onSelectCoin(symbol);
    setIsOpen(false);
    setSearchValue('');
  };

  const handleToggle = () => {
    setIsOpen(!isOpen);
    if (isOpen) {
      setSearchValue('');
    }
  };

  return (
    <S.Wrapper ref={dropdownRef} onClick={handleToggle}>
      <S.CoinInfo>
        <S.CoinIcon src={coinDetail?.image} alt="cryptocurrency" />
        <S.CoinName>{`${coinDetail?.name} (${coinDetail?.symbol})`}</S.CoinName>
      </S.CoinInfo>
      <S.ArrowButton $isOpen={isOpen}>
        <svg
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M7 10L12 15"
            stroke="white"
            strokeWidth="1.6"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M12 15L17 10"
            stroke="white"
            strokeWidth="1.6"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </S.ArrowButton>

      {isOpen && (
        <S.Dropdown onClick={(e) => e.stopPropagation()}>
          <S.SearchWrapper>
            <S.SearchIcon>
              <svg
                viewBox="0 0 20 20"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M9.16667 15.8333C12.8486 15.8333 15.8333 12.8486 15.8333 9.16667C15.8333 5.48477 12.8486 2.5 9.16667 2.5C5.48477 2.5 2.5 5.48477 2.5 9.16667C2.5 12.8486 5.48477 15.8333 9.16667 15.8333Z"
                  stroke="#b3b5bd"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M17.5 17.5L13.875 13.875"
                  stroke="#b3b5bd"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </S.SearchIcon>
            <S.SearchInput
              ref={searchInputRef}
              type="text"
              placeholder="Search"
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
            />
          </S.SearchWrapper>
          <S.DropdownList>
            {filteredCoinList.map((coin) => (
              <S.DropdownItem
                key={coin.id}
                onClick={() => handleSelectCoin(coin.symbol)}
                $isSelected={coin.symbol === coinDetail?.symbol}
              >
                <S.DropdownItemIcon src={coin.image} alt={coin.symbol} />
                <S.DropdownItemText
                  $isSelected={coin.symbol === coinDetail?.symbol}
                >
                  {coin.symbol}USDT Perpetual
                </S.DropdownItemText>
              </S.DropdownItem>
            ))}
          </S.DropdownList>
        </S.Dropdown>
      )}
    </S.Wrapper>
  );
}
